import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { BusinessDTO } from '../types/Business';
import { FormSelect } from '../controls/FormSelect';
import CustomInput from '../controls/CustomInput';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const BusinessFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  
  const [formData, setFormData] = useState<BusinessDTO>({
    id: undefined,
    name: '',
    entityType: '',
    countryOfOrganization: '',
    countryOfTaxResidency: '',
    businessTaxId: '',
    jurisdictionOfFormation: '',
    dateOfFormation: '',
    isPhysicalSameAsMailing: false,
    canProvideProofOfAddress: false,
    proofOfAddressExplanation: '',
    businessEmail: '',
    businessPhone: '',
    primaryContact: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    legalAddressLine1: '',
    legalAddressLine2: '',
    legalAddressCity: '',
    legalAddressState: '',
    legalAddressZipCode: '',
    legalAddressCountry: '',
    mailingAddressLine1: '',
    mailingAddressLine2: '',
    mailingAddressCity: '',
    mailingAddressState: '',
    mailingAddressZipCode: '',
    mailingAddressCountry: '',
    registeredAgentName: '',
    registeredAgentEmail: '',
    registeredAgentPhone: '',
    registeredAgentAddressLine1: '',
    registeredAgentAddressLine2: '',
    registeredAgentAddressCity: '',
    registeredAgentAddressState: '',
    registeredAgentAddressZipCode: '',
    registeredAgentAddressCountry: '',
    beneficialOwners: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadBusiness = async (businessId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get<{ business: BusinessDTO }>(`/api/business/getbusiness?businessId=${businessId}`);
        setFormData(response.data.business);
      } catch (error) {
        console.error('Error loading business:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we're editing an existing business
    const businessId = new URLSearchParams(location.search).get('businessId');
    if (businessId) {
      setIsEditing(true);
      loadBusiness(parseInt(businessId));
    }
  }, [location.search, api, familyId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBeneficialOwnerChange = (index: number, field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.map((owner, i) => 
        i === index ? { ...owner, [field]: value } : owner
      )
    }));
  };

  const addBeneficialOwner = () => {
    setFormData(prev => ({
      ...prev,
      beneficialOwners: [
        ...prev.beneficialOwners,
        {
          id: 0,
          name: '',
          email: '',
          phone: '',
          percentageOwnership: 0
        }
      ]
    }));
  };

  const removeBeneficialOwner = (index: number) => {
    setFormData(prev => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.filter((_, i) => i !== index)
    }));
  };

  const handleFieldChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!familyId) return;

    try {
      setIsLoading(true);
      
      // Remove frontend-only fields that the backend doesn't expect
      const cleanedFormData = { ...formData };
      delete cleanedFormData.deleted;
      
      if (isEditing) {
        await api.put(`/api/business/updatebusiness?familyId=${familyId}`, cleanedFormData);
      } else {
        await api.post(`/api/business/createbusiness?familyId=${familyId}`, cleanedFormData);
      }
      
      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving business:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading || (isEditing && !formData)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Business' : 'Add New Business'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update business information' : 'Enter business information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CustomInput
                  label="Business Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFieldChange('name')}
                  required
                />
              </div>
              <div>
                <FormSelect
                  label="Entity Type"
                  value={formData.entityType || null}
                  options={[
                    { value: 'LLC', label: 'LLC' },
                    { value: 'Corporation', label: 'Corporation' },
                    { value: 'Partnership', label: 'Partnership' },
                    { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
                  ]}
                  onChange={(val) => setFormData(prev => ({ ...prev, entityType: (val as string) ?? '' }))}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Country of Organization"
                  name="countryOfOrganization"
                  value={formData.countryOfOrganization}
                  onChange={handleFieldChange('countryOfOrganization')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Country of Tax Residency"
                  name="countryOfTaxResidency"
                  value={formData.countryOfTaxResidency}
                  onChange={handleFieldChange('countryOfTaxResidency')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Business Tax ID"
                  name="businessTaxId"
                  value={formData.businessTaxId}
                  onChange={handleFieldChange('businessTaxId')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Jurisdiction of Formation"
                  name="jurisdictionOfFormation"
                  value={formData.jurisdictionOfFormation}
                  onChange={handleFieldChange('jurisdictionOfFormation')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Date of Formation"
                  name="dateOfFormation"
                  type="date"
                  value={formData.dateOfFormation}
                  onChange={handleFieldChange('dateOfFormation')}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    name="canProvideProofOfAddress"
                    checked={formData.canProvideProofOfAddress}
                    onChange={handleInputChange}
                    className="h-4 w-4 custom-checkbox"
                  />
                  <label className="ml-2 block text-sm font-medium text-gray-700">
                    Can provide proof of address
                  </label>
                </div>
                {!formData.canProvideProofOfAddress && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Proof of Address Explanation</label>
                    <textarea
                      name="proofOfAddressExplanation"
                      value={formData.proofOfAddressExplanation}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Please explain why proof of address cannot be provided..."
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CustomInput
                  label="Business Email"
                  name="businessEmail"
                  type="email"
                  value={formData.businessEmail}
                  onChange={handleFieldChange('businessEmail')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Business Phone"
                  name="businessPhone"
                  type="tel"
                  value={formData.businessPhone}
                  onChange={handleFieldChange('businessPhone')}
                />
              </div>
              <div>
                <CustomInput
                  label="Primary Contact"
                  name="primaryContact"
                  value={formData.primaryContact}
                  onChange={handleFieldChange('primaryContact')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Primary Contact Email"
                  name="primaryContactEmail"
                  type="email"
                  value={formData.primaryContactEmail}
                  onChange={handleFieldChange('primaryContactEmail')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Primary Contact Phone"
                  name="primaryContactPhone"
                  type="tel"
                  value={formData.primaryContactPhone}
                  onChange={handleFieldChange('primaryContactPhone')}
                />
              </div>
            </div>
          </div>

          {/* Address Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            
            {/* Legal Address */}
            <div>
              <h4 className="text-md font-medium text-gray-800 mb-3">Legal Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <CustomInput
                    label="Address Line 1"
                    name="legalAddressLine1"
                    value={formData.legalAddressLine1}
                    onChange={handleFieldChange('legalAddressLine1')}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <CustomInput
                    label="Address Line 2"
                    name="legalAddressLine2"
                    value={formData.legalAddressLine2 ?? ''}
                    onChange={handleFieldChange('legalAddressLine2')}
                  />
                </div>
                <div>
                  <CustomInput
                    label="City"
                    name="legalAddressCity"
                    value={formData.legalAddressCity}
                    onChange={handleFieldChange('legalAddressCity')}
                    required
                  />
                </div>
                <div>
                  <CustomInput
                    label="State"
                    name="legalAddressState"
                    value={formData.legalAddressState}
                    onChange={handleFieldChange('legalAddressState')}
                    required
                  />
                </div>
                <div>
                  <CustomInput
                    label="ZIP Code"
                    name="legalAddressZipCode"
                    value={formData.legalAddressZipCode}
                    onChange={handleFieldChange('legalAddressZipCode')}
                    required
                  />
                </div>
                <div>
                  <CustomInput
                    label="Country"
                    name="legalAddressCountry"
                    value={formData.legalAddressCountry}
                    onChange={handleFieldChange('legalAddressCountry')}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Mailing Address */}
            <div className="mt-6">
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  name="isPhysicalSameAsMailing"
                  checked={formData.isPhysicalSameAsMailing}
                  onChange={handleInputChange}
                  className="h-4 w-4 custom-checkbox"
                />
                <label className="ml-2 block text-sm font-medium text-gray-700">
                  Mailing address is the same as legal address
                </label>
              </div>
              
              {!formData.isPhysicalSameAsMailing && (
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Mailing Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <CustomInput
                        label="Address Line 1"
                        name="mailingAddressLine1"
                        value={formData.mailingAddressLine1}
                        onChange={handleFieldChange('mailingAddressLine1')}
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <CustomInput
                        label="Address Line 2"
                        name="mailingAddressLine2"
                        value={formData.mailingAddressLine2 ?? ''}
                        onChange={handleFieldChange('mailingAddressLine2')}
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="City"
                        name="mailingAddressCity"
                        value={formData.mailingAddressCity}
                        onChange={handleFieldChange('mailingAddressCity')}
                        required
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="State"
                        name="mailingAddressState"
                        value={formData.mailingAddressState}
                        onChange={handleFieldChange('mailingAddressState')}
                        required
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="ZIP Code"
                        name="mailingAddressZipCode"
                        value={formData.mailingAddressZipCode}
                        onChange={handleFieldChange('mailingAddressZipCode')}
                        required
                      />
                    </div>
                    <div>
                      <CustomInput
                        label="Country"
                        name="mailingAddressCountry"
                        value={formData.mailingAddressCountry}
                        onChange={handleFieldChange('mailingAddressCountry')}
                        required
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Registered Agent Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Registered Agent</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <CustomInput
                  label="Name"
                  name="registeredAgentName"
                  value={formData.registeredAgentName}
                  onChange={handleFieldChange('registeredAgentName')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Email"
                  name="registeredAgentEmail"
                  type="email"
                  value={formData.registeredAgentEmail}
                  onChange={handleFieldChange('registeredAgentEmail')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Phone"
                  name="registeredAgentPhone"
                  type="tel"
                  value={formData.registeredAgentPhone}
                  onChange={handleFieldChange('registeredAgentPhone')}
                  required
                />
              </div>
            </div>

            {/* Registered Agent Address */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-800 mb-3">Registered Agent Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <CustomInput
                    label="Address Line 1"
                    name="registeredAgentAddressLine1"
                    value={formData.registeredAgentAddressLine1}
                    onChange={handleFieldChange('registeredAgentAddressLine1')}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <CustomInput
                    label="Address Line 2"
                    name="registeredAgentAddressLine2"
                    value={formData.registeredAgentAddressLine2 ?? ''}
                    onChange={handleFieldChange('registeredAgentAddressLine2')}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
                <div>
                  <CustomInput
                    label="City"
                    name="registeredAgentAddressCity"
                    value={formData.registeredAgentAddressCity}
                    onChange={handleFieldChange('registeredAgentAddressCity')}
                    required
                  />
                </div>
                <div>
                  <CustomInput
                    label="State"
                    name="registeredAgentAddressState"
                    value={formData.registeredAgentAddressState}
                    onChange={handleFieldChange('registeredAgentAddressState')}
                    required
                  />
                </div>
                <div>
                  <CustomInput
                    label="ZIP Code"
                    name="registeredAgentAddressZipCode"
                    value={formData.registeredAgentAddressZipCode}
                    onChange={handleFieldChange('registeredAgentAddressZipCode')}
                    required
                  />
                </div>
                <div>
                  <CustomInput
                    label="Country"
                    name="registeredAgentAddressCountry"
                    value={formData.registeredAgentAddressCountry}
                    onChange={handleFieldChange('registeredAgentAddressCountry')}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Beneficial Owners Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Beneficial Owners</h3>
            <div className="space-y-4">
              {formData.beneficialOwners.length === 0 ? (
                <p className="text-gray-500 text-sm">No beneficial owners added yet.</p>
              ) : (
                formData.beneficialOwners.map((owner, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-medium text-gray-800">Beneficial Owner {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeBeneficialOwner(index)}
                        className="btn-danger btn-icon"
                        title="Remove beneficial owner"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <CustomInput
                          label="Name"
                          name={`owner-name-${index}`}
                          value={owner.name}
                          onChange={(val) => handleBeneficialOwnerChange(index, 'name', val)}
                          required
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Email"
                          name={`owner-email-${index}`}
                          type="email"
                          value={owner.email}
                          onChange={(val) => handleBeneficialOwnerChange(index, 'email', val)}
                          required
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Phone"
                          name={`owner-phone-${index}`}
                          type="tel"
                          value={owner.phone}
                          onChange={(val) => handleBeneficialOwnerChange(index, 'phone', val)}
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Percentage Ownership"
                          name={`owner-percentageOwnership-${index}`}
                          type="number"
                          value={String(owner.percentageOwnership)}
                          onChange={(val) => handleBeneficialOwnerChange(index, 'percentageOwnership', parseFloat(val) || 0)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addBeneficialOwner}
                  className="btn-primary"
                >
                  <PlusIcon />
                  Add Beneficial Owner
                </button>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Business' : 'Add Business')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BusinessFormPage;